import type { AxiosInstance } from "axios";
import { internalApiClient } from "@/marketing/infrastructure/http/httpClient";
import type {
  CommunityCategory,
  CommunityComment,
  CommunityCommentPage,
  CommunityPostStatus,
  CommunityPostType,
  CommunityPostView,
  CommunitySignedUploadUrl,
  CommunityTrendingItem,
  CommunityTrendingListing,
  CreateCategoryRequest,
  CreateCommentRequest,
  CreateGuideRequest,
  CreateManualTrendingRequest,
  CreatePickRequest,
  CreateSeedPostRequest,
  PresignImageRequest,
  UpdateCategoryRequest,
  UpdateGuideRequest,
  UpdatePickRequest,
} from "@/marketing/models/community";

const BASE_PATH = "/internal/community";

export class CommunityAdminAPIClient {
  constructor(private readonly client: AxiosInstance = internalApiClient) {}

  public async listPicks(): Promise<CommunityPostView[]> {
    const { data } = await this.client.get<CommunityPostView[]>(
      `${BASE_PATH}/picks`,
    );
    return data;
  }

  public async reorderPicks(
    orderedPostIds: string[],
  ): Promise<CommunityPostView[]> {
    const { data } = await this.client.put<CommunityPostView[]>(
      `${BASE_PATH}/pickOrder`,
      { orderedPostIds },
    );
    return data;
  }

  public async createPick(
    request: CreatePickRequest,
  ): Promise<CommunityPostView> {
    const { data } = await this.client.post<CommunityPostView>(
      `${BASE_PATH}/picks`,
      request,
    );
    return data;
  }

  public async updatePick(
    postId: string,
    request: UpdatePickRequest,
  ): Promise<CommunityPostView> {
    const { data } = await this.client.put<CommunityPostView>(
      `${BASE_PATH}/picks/${postId}`,
      request,
    );
    return data;
  }

  public async listGuides(): Promise<CommunityPostView[]> {
    const { data } = await this.client.get<CommunityPostView[]>(
      `${BASE_PATH}/guides`,
    );
    return data;
  }

  public async createGuide(
    request: CreateGuideRequest,
  ): Promise<CommunityPostView> {
    const { data } = await this.client.post<CommunityPostView>(
      `${BASE_PATH}/guides`,
      request,
    );
    return data;
  }

  public async updateGuide(
    postId: string,
    request: UpdateGuideRequest,
  ): Promise<CommunityPostView> {
    const { data } = await this.client.put<CommunityPostView>(
      `${BASE_PATH}/guides/${postId}`,
      request,
    );
    return data;
  }

  public async reorderGuides(
    orderedPostIds: string[],
  ): Promise<CommunityPostView[]> {
    const { data } = await this.client.put<CommunityPostView[]>(
      `${BASE_PATH}/guideOrder`,
      { orderedPostIds },
    );
    return data;
  }

  public async listPosts(
    type: CommunityPostType,
    status?: CommunityPostStatus,
  ): Promise<CommunityPostView[]> {
    const { data } = await this.client.get<CommunityPostView[]>(
      `${BASE_PATH}/posts`,
      { params: { type, ...(status ? { status } : {}) } },
    );
    return data;
  }

  public async moderatePost(
    postId: string,
    status: CommunityPostStatus,
  ): Promise<CommunityPostView> {
    const { data } = await this.client.put<CommunityPostView>(
      `${BASE_PATH}/posts/${postId}/status`,
      { status },
    );
    return data;
  }

  public async listPostComments(
    postId: string,
    limit?: number,
  ): Promise<CommunityCommentPage> {
    const { data } = await this.client.get<CommunityCommentPage>(
      `${BASE_PATH}/posts/${postId}/comments`,
      { params: limit ? { limit } : {} },
    );
    return data;
  }

  public async createSeedPost(
    request: CreateSeedPostRequest,
  ): Promise<CommunityPostView> {
    const { data } = await this.client.post<CommunityPostView>(
      `${BASE_PATH}/posts`,
      request,
    );
    return data;
  }

  public async listTrending(limit?: number): Promise<CommunityTrendingListing> {
    const { data } = await this.client.get<CommunityTrendingListing>(
      `${BASE_PATH}/trending`,
      { params: limit ? { limit } : {} },
    );
    return data;
  }

  public async createManualTrending(
    request: CreateManualTrendingRequest,
  ): Promise<CommunityTrendingItem> {
    const { data } = await this.client.post<CommunityTrendingItem>(
      `${BASE_PATH}/trending/manual`,
      request,
    );
    return data;
  }

  public async deleteManualTrending(contentKey: string): Promise<void> {
    await this.client.delete(
      `${BASE_PATH}/trending/manual/${encodeURIComponent(contentKey)}`,
    );
  }

  public async listCategories(): Promise<CommunityCategory[]> {
    const { data } = await this.client.get<CommunityCategory[]>(
      `${BASE_PATH}/categories`,
    );
    return data;
  }

  public async createCategory(
    request: CreateCategoryRequest,
  ): Promise<CommunityCategory> {
    const { data } = await this.client.post<CommunityCategory>(
      `${BASE_PATH}/categories`,
      request,
    );
    return data;
  }

  public async updateCategory(
    categoryId: string,
    request: UpdateCategoryRequest,
  ): Promise<CommunityCategory> {
    const { data } = await this.client.patch<CommunityCategory>(
      `${BASE_PATH}/categories/${categoryId}`,
      request,
    );
    return data;
  }

  public async reorderCategories(
    orderedCategoryIds: string[],
  ): Promise<CommunityCategory[]> {
    const { data } = await this.client.put<CommunityCategory[]>(
      `${BASE_PATH}/categoryOrder`,
      { orderedCategoryIds },
    );
    return data;
  }

  public async createComment(
    postId: string,
    request: CreateCommentRequest,
  ): Promise<CommunityComment> {
    const { data } = await this.client.post<CommunityComment>(
      `${BASE_PATH}/posts/${postId}/comments`,
      request,
    );
    return data;
  }

  public async presignImage(
    request: PresignImageRequest,
  ): Promise<CommunitySignedUploadUrl> {
    const { data } = await this.client.post<CommunitySignedUploadUrl>(
      `${BASE_PATH}/images/presignedUrl`,
      request,
    );
    return data;
  }

  // Presign → PUT the file straight to GCS → return the public CDN URL to attach to a post.
  public async uploadPostImage(authorId: number, file: File): Promise<string> {
    const ext = (file.name.split(".").pop() ?? "png").toLowerCase();
    const fileName = `${crypto.randomUUID()}.${ext}`;
    const signed = await this.presignImage({
      authorId,
      fileName,
      contentType: file.type,
      sizeBytes: file.size,
    });
    const res = await fetch(signed.presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!res.ok) {
      throw new Error(`Image upload failed (${res.status})`);
    }
    return signed.cdnUrl;
  }
}

export const communityAdminAPIClient = new CommunityAdminAPIClient();
